// Pillow 3D Controller
// - Binds <pillow-3d> success/error events
// - Provides small helpers to update attributes (size/color/src/etc.)
//
// Theme pattern: "component-*.js" for snippet/component scripts.

(() => {
  'use strict';

  const PILLOW_3D_SELECTOR = 'pillow-3d';
  const ALLOWED_SIZES = new Set([10, 16, 22, 30, 60]);

  let isBound = false; 

  // ============================================================================
  // Image Generator Module
  // ============================================================================
  const ImageGenerator = (() => {
    'use strict';

    const TRIM_SCAN_MAX_SIZE = 800;
    const ALPHA_THRESHOLD = 150;
    const PREVIEW_CANVAS_SIZE = 500;
    const PREVIEW_PADDING = 50;
    const PREVIEW_BG_COLOR = '#e1cff2';

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = src;
    });
  }

  function canvasToBlob(canvas, mimeType, quality) {
    if (canvas.convertToBlob) {
      return canvas.convertToBlob({ type: mimeType, quality: quality });
    }
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      }, mimeType, quality);
    });
  }

  function blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to convert blob to data URL'));
      reader.readAsDataURL(blob);
    });
  }

  function shouldSkipTrimming(imageUrl) {
    const url = imageUrl.toLowerCase();
    return url.endsWith('.jpg') || url.endsWith('.jpeg') || url.includes('data:image/jpeg');
  }

    // Free canvas memory
    function disposeCanvas(canvas, ctx) {
      if (!canvas) return;
      
      try {
        if (ctx && typeof ctx.clearRect === 'function') {
          ctx.clearRect(0, 0, canvas.width || 0, canvas.height || 0);
        }
        if (canvas instanceof HTMLCanvasElement) {
          canvas.width = 0;
          canvas.height = 0;
        }
      } catch (error) {
        // Ignore errors
      }
    }

    // Convert image to blob (auto-cleanup)
    async function imageToBlob(img, options = {}) {
      const {
        width = img.width,
        height = img.height,
        alpha = true
      } = options;

        const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d', { alpha });
      
      try {
        ctx.drawImage(img, 0, 0, width, height);
        return await canvasToBlob(canvas, 'image/png');
      } finally {
        disposeCanvas(canvas, ctx);
      }
    }

    // Create scan canvas (uses OffscreenCanvas if available)
    function createScanCanvas(width, height) {
      const useOffscreen = typeof OffscreenCanvas !== 'undefined';
      
      if (useOffscreen) {
        const canvas = new OffscreenCanvas(width, height);
        const ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
        return { canvas, ctx };
      }
      
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
      return { canvas, ctx };
    }

    async function trimTransparentPixelsToBlob(imageUrl) {
      let scanCanvas = null;
      let scanCtx = null;
      let out = null;
      let outCtx = null;
      
      try {
      const img = await loadImage(imageUrl);
      
        // Skip trimming for JPEG
        if (shouldSkipTrimming(imageUrl)) {
          return await imageToBlob(img);
        }

        // Downscale large images for faster scanning
      const maxDim = Math.max(img.width, img.height);
      const needsDownscale = maxDim > TRIM_SCAN_MAX_SIZE;
      const scale = needsDownscale ? TRIM_SCAN_MAX_SIZE / maxDim : 1;
      const scanWidth = Math.round(img.width * scale);
      const scanHeight = Math.round(img.height * scale);

        // Scan for transparent pixels
        const scanResult = createScanCanvas(scanWidth, scanHeight);
        scanCanvas = scanResult.canvas;
        scanCtx = scanResult.ctx;
      scanCtx.drawImage(img, 0, 0, scanWidth, scanHeight);

        let imageData;
      try {
          imageData = scanCtx.getImageData(0, 0, scanWidth, scanHeight);
      } catch (error) {
          disposeCanvas(scanCanvas, scanCtx);
          scanCanvas = null;
          scanCtx = null;
          
        if (error.name === 'SecurityError') {
            return await imageToBlob(img);
        }
        throw error;
      }

        // Find bounding box
      const { data, width, height } = imageData;
      const stride = width * 4;
      let minX = width, minY = height, maxX = -1, maxY = -1;

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const alpha = data[y * stride + x * 4 + 3];
          if (alpha > ALPHA_THRESHOLD) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

        disposeCanvas(scanCanvas, scanCtx);
        scanCanvas = null;
        scanCtx = null;

        // No opaque pixels found
      if (maxX < 0) {
          return await imageToBlob(img);
      }

        // Calculate trim bounds
      const w = maxX - minX + 1;
      const h = maxY - minY + 1;
      const origMinX = Math.round(minX / scale);
      const origMinY = Math.round(minY / scale);
      const origW = Math.round(w / scale);
      const origH = Math.round(h / scale);

        // Create trimmed canvas
        out = document.createElement('canvas');
      out.width = origW;
      out.height = origH;
        outCtx = out.getContext('2d', { 
        alpha: true,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      });

      outCtx.drawImage(img, origMinX, origMinY, origW, origH, 0, 0, origW, origH);

        const blob = await canvasToBlob(out, 'image/png');
        disposeCanvas(out, outCtx);
        return blob;
    } catch (error) {
        if (scanCanvas) disposeCanvas(scanCanvas, scanCtx);
        if (out) disposeCanvas(out, outCtx);
        
        // Fallback for security errors
      if (error.name === 'SecurityError' || error.message.includes('tainted')) {
        const img = await loadImage(imageUrl);
          return await imageToBlob(img);
      }
      throw error;
    }
  }
  
  async function createPreviewWithBackground(blob) {
      let canvas = null;
      let ctx = null;
      
      try {
        // Load image (use ImageBitmap if available)
        const img = typeof createImageBitmap !== 'undefined' 
          ? await createImageBitmap(blob)
          : await loadImage(await blobToDataURL(blob));
        
        // Calculate fit scale
        const imageArea = PREVIEW_CANVAS_SIZE - (PREVIEW_PADDING * 2);
      const scale = Math.min(imageArea / img.width, imageArea / img.height);
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
        const x = PREVIEW_PADDING + (imageArea - scaledWidth) / 2;
        const y = PREVIEW_PADDING + (imageArea - scaledHeight) / 2;
      
        // Draw on canvas
        canvas = document.createElement('canvas');
        canvas.width = PREVIEW_CANVAS_SIZE;
        canvas.height = PREVIEW_CANVAS_SIZE;
        ctx = canvas.getContext('2d');
      
        ctx.fillStyle = PREVIEW_BG_COLOR;
        ctx.fillRect(0, 0, PREVIEW_CANVAS_SIZE, PREVIEW_CANVAS_SIZE);
      ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
      
        const resultDataUrl = canvas.toDataURL('image/png');
        disposeCanvas(canvas, ctx);
        if (img.close) img.close();
        
        return resultDataUrl;
    } catch (error) {
        if (canvas) disposeCanvas(canvas, ctx);
      console.error('[Pillow3D] Failed to create preview with background:', error);
      return null;
    }
  }

  async function processPreviewImage(imageUrl) {
    const trimmedBlob = await trimTransparentPixelsToBlob(imageUrl);
    const previewNoBg = await blobToDataURL(trimmedBlob);
    const previewWithBg = await createPreviewWithBackground(trimmedBlob);

    return { 
      previewNoBg,
      previewWithBg: previewWithBg || null
    };
  }

    return {
      loadImage,
      canvasToBlob,
      blobToDataURL,
      imageToBlob,
      trimTransparentPixelsToBlob,
      createPreviewWithBackground,
      processPreviewImage
    };
  })();

  // ============================================================================
  // Pillow3D Module
  // ============================================================================
  const Pillow3D = (() => {
    'use strict';

    function getEl() {
      return $(PILLOW_3D_SELECTOR).get(0) || null;
    }


  function attach(el) {
    if (!el || isBound) return;
    isBound = true;

    const $el = $(el);

    function isColorEvent() {
      return $el.attr('data-color-event') === 'true';
    }

    function resetColorEventFlag() {
      if (isColorEvent()) $el.attr('data-color-event', 'false');
    }

    function getOptions() {
      const optionsAttr = $el.attr('data-task-options');
      if (!optionsAttr) return null;
      try {
        return JSON.parse(optionsAttr);
      } catch (e) {
        return null;
      }
    }

    $(el).on('error', (e) => {
      const original = e?.originalEvent || e;
      const detail = original?.detail || {};
      console.log('ERROR e', detail);
      e
      
      // Get all options from data-attribute and add to detail
      const options = getOptions();
      if (options) {
        detail.options = options;
      }

      resetColorEventFlag();
      $el.attr('src', '');
      $el.attr('portraitsrc', '');

      el.dispatchEvent(new CustomEvent('pillow3d:error', { bubbles: true, detail }));
    });

    $(el).on('success', async (e) => {
    
      const original = e?.originalEvent || e;
      const detail = original?.detail || null;
      console.log('PILOW_3D_ONSUCCES',e);

      if (detail) {
        // Add colorEvent flag to detail
        detail.colorEvent = isColorEvent();
        
        // Get all options from data-attribute and add to detail
        const options = getOptions();
        if (options) {
          detail.options = options;
        }
        
        if (detail.data.preview) {
          try {
            const processedImages = await ImageGenerator.processPreviewImage(detail.data.preview);
            if (processedImages) {
              detail.data.preview = processedImages.previewNoBg;
              // Add preview with background if available
              if (processedImages.previewWithBg) {
                detail.data.previewWithBg = processedImages.previewWithBg;
              }
            }
          } catch (error) {
            if (error.name !== 'SecurityError') {
              console.error('Error processing preview image:', error);
            }
          }
        }
        
        el.dispatchEvent(new CustomEvent('pillow3d:success', { bubbles: true, detail }));
        resetColorEventFlag();
      }
    });
  }

  // Set multiple attributes at once.
  function setState(state) {
    const el = getEl();
    if (!el || !state) return;

    if ('src' in state) {
      const src = state.src;
      const currentSrc = $(el).attr('src');
      
      if (src && src !== currentSrc) {
        const colorEvent = $(el).attr('data-color-event') === 'true';
        el.dispatchEvent(new CustomEvent('pillow3d:start', { 
          bubbles: true, 
          detail: {
            colorEvent: colorEvent
          }
        }));
      }
      
      if (src) $(el).attr('src', src);
      else $(el).attr('src', '');
    }

    if ('portraitSrc' in state) {
      const portraitSrc = state.portraitSrc;
      if (!portraitSrc) {
        $(el).attr('portraitsrc', '');
      } else {
        const value = typeof portraitSrc === 'string' ? portraitSrc : JSON.stringify(portraitSrc || {});
        $(el).attr('portraitsrc', value);
      }
    }

    if ('pillowData' in state) {
      const pillowData = state.pillowData;
      if (pillowData) $(el).attr('pillowdata', pillowData);
      else $(el).attr('pillowdata', '');
    }

    if ('color' in state) {
      const color = state.color;
      if (color) {
        $(el).attr('colorbg', color);

        // Only set colorEvent flag if there's no src or pillowData in state (pure color change)
        if (state.colorEvent === true && !('src' in state) && !('pillowData' in state)) {
          $(el).attr('data-color-event', 'true');
        }
      }
    }
  }

    function setSize(value) {
      const el = getEl();
      if (!el) return;

      const size = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
      if (!Number.isFinite(size) || !ALLOWED_SIZES.has(size)) return;

      $(el).attr('size', size);
  }

  function setColor(color) {
    setState({ color, colorEvent: true });
  }

  function setSrc(src) {
    setState({ src });
  }

  function setPortraitSrc(objOrJson) {
    setState({ portraitSrc: objOrJson });
  }

  function setPillowData(pillowData, color) {
    if (!color) {
      throw new Error('setPillowData: color parameter is required');
    }
    const state = { pillowData, color };
    setState(state);
  }

  function abort() {
    const el = getEl();
    if (el) {
      $(el).attr('src', '');
    }
  }

  // Public API
    // Available methods:
    // - attach(el) - Attach event listeners to pillow-3d element
    // - setState(state) - Set multiple attributes at once
    // - getEl() - Get the pillow-3d element
    // - setSize(value) - Set pillow size (10, 16, 22, 30, 60)
    // - setColor(color) - Set pillow color
    // - setSrc(src) - Set image source
    // - setPortraitSrc(objOrJson) - Set portrait source
    // - setPillowData(pillowData, color) - Set pillow data and color
    // - abort() - Abort current processing
    // - clear() - Clear src and pillow data
    return {
      attach,
      setState,
      getEl,
      setSize,
      setColor,
      setSrc,
      setPortraitSrc,
      setPillowData,
      abort
    };
  })();

  // Initialize
  const init = () => {
    Pillow3D.attach(Pillow3D.getEl());
  };

  $(init);

  window.Pillow3D = {
    getEl: () => Pillow3D.getEl(),
    setSrc: (src) => Pillow3D.setSrc(src),
    setColor: (color) => Pillow3D.setColor(color),
    setPillowData: (pillowData, color) => Pillow3D.setPillowData(pillowData, color),
    abort: () => Pillow3D.abort()
  };
})();
